import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import pacientesService, { Consulta } from '../../services/pacientesService';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon } from 'lucide-react';
import RecipePrintCanvas, {
  DEFAULT_FIELD_POSITIONS,
  FieldPosition,
  RecipeFieldKey,
  RECIPE_FIELD_KEYS,
} from '../../components/receta/RecipePrintCanvas';

const CANVAS_WIDTH = 1400; // 14 cm expressed in hundredths of cm
const CANVAS_HEIGHT = 2670; // 26.7 cm expressed in hundredths of cm
const PX_TO_CANVAS_UNITS = 2.64583333; // 0.0264583333 * 100
const BASE_KEY_STEP = Math.max(1, Math.round(PX_TO_CANVAS_UNITS));

const createDefaultLayout = () =>
  RECIPE_FIELD_KEYS.reduce<Record<RecipeFieldKey, FieldPosition>>((acc, key) => {
    acc[key] = { ...DEFAULT_FIELD_POSITIONS[key] };
    return acc;
  }, {} as Record<RecipeFieldKey, FieldPosition>);

const calcularEdadDetalle = (fechaNacimiento: string) => {
  const nacimiento = new Date(fechaNacimiento);
  if (Number.isNaN(nacimiento.getTime())) {
    return { anios: '', meses: '', dias: '' };
  }

  const hoy = new Date();
  let y = hoy.getFullYear() - nacimiento.getFullYear();
  let m = hoy.getMonth() - nacimiento.getMonth();
  let d = hoy.getDate() - nacimiento.getDate();

  if (d < 0) {
    const prevMonth = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    d += prevMonth.getDate();
    m -= 1;
  }
  if (m < 0) {
    m += 12;
    y -= 1;
  }

  return {
    anios: Math.max(0, y).toString(),
    meses: Math.max(0, m).toString(),
    dias: Math.max(0, d).toString(),
  };
};

const formatFechaImpresion = (fecha: string) => {
  const date = new Date(fecha);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const parts = new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const month = get('month').replace('.', '').toUpperCase();
  return `${get('day')} ${month} ${get('year')}`;
};

const buildPrescriptionText = (consulta: Consulta) => {
  const partes: string[] = [];

  if (consulta.medicamentos && consulta.medicamentos.length > 0) {
    const medicamentosTexto = consulta.medicamentos
      .map((med) => {
        const lineas: string[] = [];
        if (med.medicamento) {
          lineas.push(med.medicamento.toUpperCase());
        }

        const detalles = [med.dosis, med.frecuencia, med.duracion].filter(Boolean).join(' · ');
        if (detalles) {
          lineas.push(detalles);
        }

        if (med.indicaciones) {
          lineas.push(med.indicaciones);
        }

        return lineas.join('\n');
      })
      .filter(Boolean)
      .join('\n\n');

    if (medicamentosTexto) {
      partes.push(medicamentosTexto);
    }
  }

  if (consulta.observaciones) {
    partes.push(consulta.observaciones);
  }

  return partes.join('\n\n');
};

export default function RecetaPage() {
  const navigate = useNavigate();
  const { consultaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [consulta, setConsulta] = useState<Consulta | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedField, setSelectedField] = useState<RecipeFieldKey | null>(null);
  const [fieldPositions, setFieldPositions] = useState<Partial<Record<RecipeFieldKey, FieldPosition>>>({});
  const [fieldDimensions, setFieldDimensions] = useState<
    Partial<Record<RecipeFieldKey, { width: number; height: number }>>
  >({});
  const [initialLayout, setInitialLayout] = useState<Record<RecipeFieldKey, FieldPosition>>(createDefaultLayout);

  const handleFieldBoundsChange = useCallback(
    (field: RecipeFieldKey, size: { width: number; height: number }) => {
      const widthUnits = Math.max(0, Math.round(size.width * PX_TO_CANVAS_UNITS));
      const heightUnits = Math.max(0, Math.round(size.height * PX_TO_CANVAS_UNITS));

      setFieldDimensions((prev) => {
        const existing = prev[field];
        if (existing && existing.width === widthUnits && existing.height === heightUnits) {
          return prev;
        }

        return {
          ...prev,
          [field]: { width: widthUnits, height: heightUnits },
        };
      });
    },
    [],
  );

  const updateFieldPosition = (field: RecipeFieldKey, updater: (current: FieldPosition) => FieldPosition) => {
    setFieldPositions((prev) => {
      const base = DEFAULT_FIELD_POSITIONS[field];
      const current = { ...base, ...prev[field] };
      const updated = updater(current);

      const sanitized: FieldPosition = {
        top: Math.round(updated.top),
        left: Math.round(updated.left),
      };

      const sameAsDefault = sanitized.top === base.top && sanitized.left === base.left;

      if (sameAsDefault) {
        const next = { ...prev };
        delete next[field];
        return next;
      }

      return { ...prev, [field]: sanitized };
    });
  };

  const handleToggleEdit = () => {
    setEditMode((prev) => {
      if (prev) {
        setSelectedField(null);
      }
      return !prev;
    });
  };

  const handleResetPositions = () => {
    setFieldPositions({});
    setSelectedField(null);
    toast.success('Posiciones restablecidas');
  };

  const handleSavePositions = async () => {
    try {
      const mergedLayout = RECIPE_FIELD_KEYS.reduce<Record<RecipeFieldKey, FieldPosition>>((acc, key) => {
        const base = DEFAULT_FIELD_POSITIONS[key];
        const override = fieldPositions[key];
        acc[key] = {
          ...base,
          ...(override ?? {}),
        };
        return acc;
      }, {} as Record<RecipeFieldKey, FieldPosition>);

      const hasChanges = RECIPE_FIELD_KEYS.some((key) => {
        const next = mergedLayout[key];
        const prev = initialLayout[key];
        return (
          next.top !== prev.top ||
          next.left !== prev.left ||
          (next.width ?? null) !== (prev.width ?? null)
        );
      });

      if (!hasChanges) {
        toast.error('No hay cambios para guardar');
        return;
      }

      const positions = RECIPE_FIELD_KEYS.map((campo) => ({
        campo,
        top: mergedLayout[campo].top,
        left: mergedLayout[campo].left,
        width: mergedLayout[campo].width,
      }));

      await pacientesService.updateRecetaLayout({ formato: 'default', positions });
      toast.success('Posiciones guardadas exitosamente');
      setInitialLayout(mergedLayout);
    } catch (error) {
      console.error('Error al guardar posiciones:', error);
      toast.error('Error al guardar posiciones');
    }
  };

  useEffect(() => {
    loadReceta();
    loadLayout();
  }, [consultaId]);

  const loadLayout = async () => {
    try {
      const layouts = await pacientesService.getRecetaLayout();
      const layoutMap = layouts.reduce<Partial<Record<RecipeFieldKey, FieldPosition>>>((acc, layout) => {
        const key = layout.campo as RecipeFieldKey;
        if (!RECIPE_FIELD_KEYS.includes(key)) {
          return acc;
        }
        acc[key] = {
          top: layout.top,
          left: layout.left,
          ...(layout.width != null ? { width: layout.width } : {}),
        };
        return acc;
      }, {});

      const mergedLayout = RECIPE_FIELD_KEYS.reduce<Record<RecipeFieldKey, FieldPosition>>((acc, key) => {
        const base = DEFAULT_FIELD_POSITIONS[key];
        const override = layoutMap[key];
        acc[key] = {
          ...base,
          ...(override ?? {}),
        };
        return acc;
      }, {} as Record<RecipeFieldKey, FieldPosition>);

      const overrides = RECIPE_FIELD_KEYS.reduce<Partial<Record<RecipeFieldKey, FieldPosition>>>((acc, key) => {
        const base = DEFAULT_FIELD_POSITIONS[key];
        const next = mergedLayout[key];
        if (
          next.top !== base.top ||
          next.left !== base.left ||
          (next.width ?? null) !== (base.width ?? null)
        ) {
          acc[key] = { ...next };
        }
        return acc;
      }, {});

      setInitialLayout(mergedLayout);
      setFieldPositions(overrides);
    } catch (error) {
      console.error('Error al cargar layout:', error);
    }
  };

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedField) {
        return;
      }

      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) {
        return;
      }

      event.preventDefault();

      const multiplier = event.shiftKey ? 5 : 1;
      const delta = BASE_KEY_STEP * multiplier;

      updateFieldPosition(selectedField, (current) => {
        const dimension = fieldDimensions[selectedField];
        const fallbackWidth = current.width ?? DEFAULT_FIELD_POSITIONS[selectedField].width ?? 0;
        const width = dimension?.width ?? fallbackWidth;
        const height = dimension?.height ?? 0;
        let nextTop = current.top;
        let nextLeft = current.left;

        switch (event.key) {
          case 'ArrowUp':
            nextTop -= delta;
            break;
          case 'ArrowDown':
            nextTop += delta;
            break;
          case 'ArrowLeft':
            nextLeft -= delta;
            break;
          case 'ArrowRight':
            nextLeft += delta;
            break;
          default:
            break;
        }

        const maxTop = height > 0 ? Math.max(0, CANVAS_HEIGHT - height) : CANVAS_HEIGHT;
        const clampedTop = Math.min(Math.max(nextTop, 0), maxTop);
        const maxLeft = width > 0 ? Math.max(0, CANVAS_WIDTH - width) : CANVAS_WIDTH;
        const clampedLeft = Math.min(Math.max(nextLeft, 0), maxLeft);

        return {
          ...current,
          top: clampedTop,
          left: clampedLeft,
        };
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, fieldDimensions, selectedField]);

  const loadReceta = async () => {
    try {
      setLoading(true);
      const data = await pacientesService.getReceta(Number(consultaId));
      setConsulta(data);
    } catch (error) {
      console.error('Error al cargar receta:', error);
      toast.error('Error al cargar receta');
      navigate('/pacientes');
    } finally {
      setLoading(false);
    }
  };

  const calcularEdad = (fechaNacimiento: string) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    if (edad < 1) {
      const meses = mes < 0 ? 12 + mes : mes;
      return `${meses} meses`;
    }
    
    return `${edad} años`;
  };

  const formatFecha = (fecha: string) => {
    const date = new Date(fecha);
    const parts = new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const month = get('month').replace('.', '').toUpperCase();
    return `${get('day')} ${month} ${get('year')}`;
  };

  const handlePrint = () => {
    setEditMode(false);
    setSelectedField(null);
    window.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!consulta || !consulta.paciente) {
    return null;
  }

  const edadDetalle = calcularEdadDetalle(consulta.paciente.fechaNacimiento);
  const fechaImpresion = formatFechaImpresion(consulta.fecha);
  const pesoTexto = consulta.peso != null ? `${consulta.peso}` : '';
  const estaturaTexto = consulta.estatura != null ? `${consulta.estatura}` : '';
  const temperaturaTexto = consulta.temperatura != null ? `${consulta.temperatura}` : '';
  const textoPrescripcion = buildPrescriptionText(consulta);

  const patientName = consulta.paciente.nombre?.toUpperCase() ?? '';

  return (
    <div className="max-w-6xl mx-auto print:max-w-none print:mx-0">
      {/* Botones de acción - No se imprimen */}
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pacientes')}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Volver
          </button>
          <button
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              editMode
                ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                : 'text-blue-700 border-blue-200 hover:bg-blue-50'
            }`}
          >
            {editMode ? 'Salir modo edición' : 'Editar posiciones'}
          </button>
          {editMode && (
            <>
              <button
                onClick={handleSavePositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Guardar posiciones
              </button>
              <button
                onClick={handleResetPositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Restablecer posiciones
              </button>
            </>
          )}
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PrinterIcon className="w-5 h-5" />
          Imprimir Receta
        </button>
      </div>

      {/* Vista previa unificada */}
      <div className="print:hidden">
        {editMode && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm space-y-1">
            <p className="font-semibold text-blue-900">Modo edición activo</p>
            <p>Selecciona un campo y usa las flechas para moverlo 1px.</p>
            <p>Mantén Shift para mover de 5px en 5px. Pulsa "Restablecer posiciones" para volver al diseño original.</p>
          </div>
        )}
        <div className="recipe-preview-shell">
          <RecipePrintCanvas
            date={fechaImpresion}
            patientName={patientName}
            ageYears={edadDetalle.anios}
            ageMonths={edadDetalle.meses}
            ageDays={edadDetalle.dias}
            weight={pesoTexto}
            height={estaturaTexto}
            temperature={temperaturaTexto}
            prescription={textoPrescripcion}
            editMode={editMode}
            selectedField={selectedField}
            onSelectField={setSelectedField}
            fieldPositions={fieldPositions}
            onFieldBoundsChange={handleFieldBoundsChange}
          />
        </div>

        {/* Información complementaria */}
        <div className="mt-8 grid grid-cols-1 gap-6 text-sm text-gray-700">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumen de consulta</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Paciente</p>
                <p className="font-medium text-gray-900">{consulta.paciente.nombre}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Edad</p>
                <p className="font-medium text-gray-900">{calcularEdad(consulta.paciente.fechaNacimiento)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Sexo</p>
                <p className="font-medium text-gray-900">{consulta.paciente.sexo === 'M' ? 'Masculino' : 'Femenino'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Fecha consulta</p>
                <p className="font-medium text-gray-900">{formatFecha(consulta.fecha)}</p>
              </div>
              {consulta.peso && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Peso</p>
                  <p className="font-medium text-gray-900">{consulta.peso} kg</p>
                </div>
              )}
              {consulta.estatura && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Estatura</p>
                  <p className="font-medium text-gray-900">{consulta.estatura} cm</p>
                </div>
              )}
              {consulta.temperatura && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Temperatura</p>
                  <p className="font-medium text-gray-900">{consulta.temperatura}°C</p>
                </div>
              )}
            </div>
          </div>

          {consulta.diagnosticos && consulta.diagnosticos.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Diagnósticos</h2>
              <ul className="space-y-2 text-gray-800">
                {consulta.diagnosticos.map((diag, index) => (
                  <li key={diag.id}>
                    <span className="font-semibold text-gray-900 mr-2">{index + 1}.</span>
                    {diag.diagnostico}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {consulta.observaciones && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Indicaciones generales</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{consulta.observaciones}</p>
            </div>
          )}
        </div>
      </div>

      <div className="hidden print:block">
        <RecipePrintCanvas
          date={fechaImpresion}
          patientName={patientName}
          ageYears={edadDetalle.anios}
          ageMonths={edadDetalle.meses}
          ageDays={edadDetalle.dias}
          weight={pesoTexto}
          height={estaturaTexto}
          temperature={temperaturaTexto}
          prescription={textoPrescripcion}
          fieldPositions={fieldPositions}
          onFieldBoundsChange={handleFieldBoundsChange}
        />
      </div>
    </div>
  );
}
